/**
 * IRRA PDF Report Generation
 *
 * Uses shared assessment PDF utilities to generate professional
 * ransomware resilience assessment reports.
 */

import type { IRRAResult } from './InteractiveRansomwareResilienceAssessment';
import {
    createAssessmentPDF,
    addTitlePage,
    addSection,
    addSubsection,
    addTable,
    addBulletList,
    addContactSection,
    addPageNumbers,
    addDisclaimer,
    downloadPDF,
    checkPageBreak,
    COLORS,
    getPageDimensions,
    formatDate,
} from '@/lib/assessment-common/pdf';
import type { jsPDFWithPlugin } from '@/lib/assessment-common/pdf';

/**
 * Generate and download IRRA assessment report PDF.
 *
 * Async because `createAssessmentPDF()` now loads jsPDF at runtime. It rejects
 * if the library cannot be reached, so the caller must await and handle that —
 * never `void` it, or a CDN failure becomes a button that does nothing.
 */
export async function generateIRRAPDF(result: IRRAResult, companyName?: string): Promise<void> {
    const doc = await createAssessmentPDF();

    // Title Page
    const details: Record<string, string> = {
        'Assessment Type': 'Ransomware Resilience Assessment (IRRA)',
        'Report Generated': formatDate(),
    };

    if (companyName) {
        details['Organization'] = companyName;
    }

    addTitlePage(
        doc,
        'Interactive Ransomware',
        'Resilience Assessment',
        'Strategic Recovery Readiness Report',
        details,
        'Overall Recovery Maturity',
        `${result.totalScore}% - ${result.maturity.label.replace(/Level \d+: /, '')}`
    );

    // Start new page for content
    doc.addPage();
    let yPos = 20;

    // Executive Summary
    yPos = addSection(doc, 'Executive Summary', yPos, { addSpace: false });

    doc.setFontSize(11);
    doc.setTextColor(...COLORS.GRAY_700);
    doc.setFont('helvetica', 'normal');
    const summaryText = `This ransomware resilience assessment evaluated your organization's recovery posture across four critical pillars: Data Protection Principles, Mission Criticality & Performance, Recovery Validation & IR Readiness, and SaaS & Cloud Resilience. Your overall maturity score of ${result.totalScore}% places you at ${result.maturity.label}.`;
    yPos = doc.splitTextToSize(summaryText, 170).length * 5 + yPos;
    doc.text(doc.splitTextToSize(summaryText, 170), 20, yPos);
    yPos += 8;

    // Maturity Level Description
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.GRAY_600);
    doc.setFont('helvetica', 'italic');
    yPos = doc.splitTextToSize(result.maturity.description, 170).length * 5 + yPos;
    doc.text(doc.splitTextToSize(result.maturity.description, 170), 20, yPos);
    yPos += 15;

    // Module Scores Section
    yPos = checkPageBreak(doc, yPos, 80);
    yPos = addSubsection(doc, 'Pillar Breakdown', yPos);

    const moduleRows = result.moduleScores.map((module) => [
        module.label,
        module.score !== null ? `${module.score}%` : 'Not Scored',
        module.score !== null
            ? module.score >= 75
                ? 'Strong'
                : module.score >= 45
                ? 'Developing'
                : 'Critical'
            : 'N/A',
    ]);

    yPos = addTable(
        doc,
        ['Pillar', 'Score', 'Status'],
        moduleRows,
        yPos,
        {
            theme: 'grid',
            headColor: COLORS.BLUE_600,
            fontSize: 10,
            columnWidths: { 0: 90, 1: 40, 2: 40 },
            columnAligns: { 1: 'center', 2: 'center' },
            boldColumns: [1],
        }
    );
    yPos += 15;

    // Critical Alerts Section
    if (result.criticalAlerts.length > 0 || (result.rtoDelta !== undefined && result.rtoDelta > 0)) {
        yPos = checkPageBreak(doc, yPos, 60);
        yPos = addSection(doc, 'Critical Risk Alerts', yPos, { color: COLORS.RED_600 });

        const alerts: string[] = [];

        result.criticalAlerts.forEach((alert) => {
            alerts.push(alert.message);
        });

        if (result.rtoDelta !== undefined && result.rtoDelta > 0 && result.targetHours !== undefined && result.actualHours !== undefined) {
            alerts.push(
                `RTO Delta Gap: Your tested recovery time is ${result.actualHours.toFixed(1)} hours, exceeding the business target of ${result.targetHours.toFixed(1)} hours by ${result.rtoDelta.toFixed(1)} hours. This is a critical failure of assurance and should be remediated before the next audit.`
            );
        }

        yPos = addBulletList(doc, alerts, 25, yPos, 165, '!');
        yPos += 10;
    }

    // Remediation Roadmap Section
    yPos = checkPageBreak(doc, yPos, 80);
    yPos = addSection(doc, 'Personalized Recovery Roadmap', yPos);

    if (result.remediation.length === 0) {
        doc.setFontSize(10);
        doc.setTextColor(...COLORS.GRAY_600);
        doc.setFont('helvetica', 'normal');
        doc.text(
            'No critical weaknesses detected. Keep validating your playbooks and expanding automation to remain resilient.',
            20,
            yPos
        );
        yPos += 15;
    } else {
        doc.setFontSize(10);
        doc.setTextColor(...COLORS.GRAY_700);
        doc.setFont('helvetica', 'normal');
        doc.text(
            `Top ${result.remediation.length} priorities ranked by risk severity and gap size:`,
            20,
            yPos
        );
        yPos += 10;

        const remediationRows = result.remediation.map((item, index) => [
            `${index + 1}`,
            item.title,
            item.severity.toUpperCase(),
            item.link.label,
        ]);

        yPos = addTable(
            doc,
            ['#', 'Remediation Item', 'Priority', 'Learn More'],
            remediationRows,
            yPos,
            {
                theme: 'striped',
                headColor: COLORS.BLUE_600,
                fontSize: 9,
                columnWidths: { 0: 10, 1: 90, 2: 30, 3: 40 },
                columnAligns: { 0: 'center', 2: 'center' },
                boldColumns: [1],
            }
        );
        yPos += 15;
    }

    // Recommendations Section
    yPos = checkPageBreak(doc, yPos, 60);
    yPos = addSection(doc, 'Next Steps', yPos);

    const recommendations = [
        'Schedule quarterly ransomware recovery drills with cross-functional teams',
        'Validate that immutable backup copies meet the 3-2-1-1 standard',
        'Document and test RTO/RPO targets for all Tier-1 applications',
        'Ensure SaaS data (Microsoft 365, Google Workspace) has dedicated backup',
        'Conduct executive tabletop exercises to validate incident response playbooks',
    ];

    yPos = addBulletList(doc, recommendations, 25, yPos, 165);
    yPos += 15;

    // Contact Section
    yPos = checkPageBreak(doc, yPos, 80);
    yPos = addContactSection(
        doc,
        yPos,
        'Ready to Close Your Gaps?',
        'Our ransomware recovery architects can help you:',
        [
            'Implement 3-2-1-1 backup architecture with immutability',
            'Align RTO/RPO targets to business impact and test recovery',
            'Build ransomware-specific incident response playbooks',
            'Protect SaaS data with dedicated backup solutions',
        ]
    );

    // Add page numbers (skip title page)
    addPageNumbers(doc, 'Page', 2);

    // Add disclaimer on last page
    const { height } = getPageDimensions(doc);
    if (yPos > height - 70) {
        doc.addPage();
    }
    addDisclaimer(
        doc,
        'DISCLAIMER: This ransomware resilience assessment provides a general overview based on self-reported information. ' +
        'It does not constitute a formal security audit, business continuity certification, or legal advice. Actual recovery ' +
        'requirements may vary based on your specific industry regulations, contractual obligations, and risk tolerance. ' +
        'Consult with qualified professionals and legal counsel for formal guidance on disaster recovery and incident response planning.'
    );

    // Download PDF
    const filename = `ransomware-resilience-assessment-${new Date().toISOString().split('T')[0]}`;
    downloadPDF(doc, filename);
}
